import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Copy, Check, ExternalLink, KeyRound, Link2, RefreshCw,
  FileText, Download, Loader2, Zap, Terminal, Calendar, Package, FileDigit,
} from "lucide-react";
import Layout from "@/components/Layout";
import { api, API, downloadArquivoUrl } from "@/lib/api";

function Copyable({ value, testid, mono = true }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Copiado!");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5 group">
      <span className={`flex-1 truncate text-sm ${mono ? "font-mono" : ""} text-zinc-700`}>{value}</span>
      <button onClick={copy} data-testid={testid} className="text-zinc-400 hover:text-orange-500 transition shrink-0">
        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function curlExemplo(apiBase, apiKey, competencia) {
  const body = {
    competencia: competencia || "2026-05",
    substituir: true,
    notas: [
      {
        cnpj_participante: "11222333000181",
        ie_participante: "0623079040081",
        data: "2026-05-10",
        modelo: "55", serie: "1", numero: "1024", cfop: "5102", emitente: "P",
        valor_total: 250.0, base_icms: 250.0, valor_icms: 45.0, isentas: 0, outras: 0,
        aliquota: 18.0, situacao: "N",
        itens: [
          { numero_item: 1, codigo_produto: "P001", cst: "000", cfop: "5102",
            quantidade: 5, valor_produto: 250.0, base_icms: 250.0, aliquota_icms: 18.0 },
        ],
      },
    ],
    produtos: [
      { codigo: "P001", ncm: "22030000", descricao: "PRODUTO EXEMPLO",
        unidade: "UN", aliquota_icms: 18.0 },
    ],
  };
  return `curl -X POST "${apiBase}/erp/ingest" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body, null, 2)}'`;
}

export default function EmpresaDetalhe() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [competencia, setCompetencia] = useState("");
  const [dados, setDados] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [showCurl, setShowCurl] = useState(false);

  const linkContador = emp ? `${window.location.origin}${process.env.PUBLIC_URL || ""}/contador/${emp.link_token}` : "";

  const carregar = async () => {
    const e = await api.obterEmpresa(id);
    setEmp(e);
    setArquivos(await api.listarArquivos(id));
    if (!competencia) {
      const now = new Date();
      setCompetencia(e.competencias?.[0] || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    }
  };

  useEffect(() => {
    carregar().catch(() => toast.error("Erro ao carregar empresa"));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (competencia) api.dadosEmpresa(id, competencia).then(setDados).catch(() => {});
  }, [competencia, id]);

  const gerar = async () => {
    if (!competencia) return toast.error("Selecione a competência");
    setGerando(true);
    try {
      await api.gerar(id, competencia);
      toast.success("Arquivo SINTEGRA gerado!");
      setArquivos(await api.listarArquivos(id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar");
    } finally {
      setGerando(false);
    }
  };

  const regenerar = async () => {
    if (!window.confirm("Gerar novas chaves? O link e a API key atuais deixarão de funcionar.")) return;
    await api.regenerarChaves(id);
    toast.success("Chaves regeneradas");
    carregar();
  };

  if (!emp)
    return (
      <Layout>
        <div className="flex justify-center py-24 text-zinc-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      </Layout>
    );

  const totalNotas = dados?.notas?.length || 0;
  const totalProdutos = dados?.produtos?.length || 0;

  return (
    <Layout>
      <div className="fade-up space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-orange-600 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>

        {/* Header */}
        <div className="bg-[#1c1c1f] grain rounded-2xl p-6 md:p-8 text-white">
          <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">{emp.razao_social}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-zinc-300 font-mono">
            <span>CNPJ {emp.cnpj}</span>
            <span>IE {emp.ie}</span>
            <span>{emp.municipio}/{emp.uf}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Integração ERP */}
          <section className="bg-white rounded-2xl p-6 border border-zinc-100">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-5 h-5 text-orange-500" />
              <h2 className="font-display font-bold text-lg">Integração com o ERP</h2>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Seu ERP Laravel envia os dados fiscais via POST para o endpoint abaixo.</p>

            <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wide">Endpoint (POST)</label>
            <Copyable value={`${API}/erp/ingest`} testid="copy-endpoint" />

            <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wide mt-4">
              X-API-Key (secreta)
            </label>
            <Copyable value={emp.api_key} testid="copy-apikey" />

            <button
              onClick={() => setShowCurl(!showCurl)}
              data-testid="toggle-curl-btn"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              <Terminal className="w-4 h-4" /> {showCurl ? "Ocultar" : "Ver"} exemplo de requisição
            </button>
            {showCurl && (
              <pre className="mt-3 bg-[#1c1c1f] text-emerald-300 text-xs rounded-xl p-4 overflow-x-auto max-h-72 leading-relaxed" data-testid="curl-exemplo">
                {curlExemplo(API, emp.api_key, competencia)}
              </pre>
            )}
          </section>

          {/* Link do contador */}
          <section className="bg-white rounded-2xl p-6 border border-zinc-100">
            <div className="flex items-center gap-2 mb-1">
              <Link2 className="w-5 h-5 text-orange-500" />
              <h2 className="font-display font-bold text-lg">Portal do Contador</h2>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Envie este link ao contador. Ele acessa e baixa os arquivos direto — sem e-mail ou WhatsApp.
            </p>

            <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wide">Link exclusivo</label>
            <Copyable value={linkContador} testid="copy-link-contador" />

            <div className="flex gap-3 mt-4">
              <a
                href={linkContador}
                target="_blank"
                rel="noreferrer"
                data-testid="abrir-portal-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition"
              >
                <ExternalLink className="w-4 h-4" /> Abrir portal
              </a>
              <button
                onClick={regenerar}
                data-testid="regenerar-chaves-btn"
                className="inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-zinc-200 text-zinc-600 font-semibold text-sm hover:bg-zinc-50 transition"
              >
                <RefreshCw className="w-4 h-4" /> Regenerar
              </button>
            </div>
          </section>
        </div>

        {/* Geração */}
        <section className="bg-white rounded-2xl p-6 border border-zinc-100">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-orange-500" />
            <h2 className="font-display font-bold text-lg">Gerar arquivo SINTEGRA</h2>
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wide">Competência</label>
              <input
                type="month"
                value={competencia}
                onChange={(e) => setCompetencia(e.target.value)}
                data-testid="competencia-input"
                className="px-3.5 py-2.5 rounded-lg border border-zinc-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none text-sm font-mono"
              />
            </div>
            <div className="flex gap-3 flex-1">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-50 text-sm text-zinc-600">
                <FileText className="w-4 h-4 text-orange-500" /> <b>{totalNotas}</b> notas
              </div>
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-50 text-sm text-zinc-600">
                <Package className="w-4 h-4 text-orange-500" /> <b>{totalProdutos}</b> produtos
              </div>
            </div>
            <button
              onClick={gerar}
              disabled={gerando}
              data-testid="gerar-btn"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-[#1c1c1f] hover:bg-orange-500 text-white font-semibold transition-colors disabled:opacity-60"
            >
              {gerando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              Gerar arquivo
            </button>
          </div>
          {totalNotas === 0 && totalProdutos === 0 && (
            <p className="text-xs text-amber-600 mt-3 bg-amber-50 rounded-lg px-3 py-2 inline-block">
              Sem dados para esta competência. Envie os registros pelo ERP antes de gerar.
            </p>
          )}
        </section>

        {/* Arquivos gerados */}
        <section className="bg-white rounded-2xl p-6 border border-zinc-100">
          <div className="flex items-center gap-2 mb-4">
            <FileDigit className="w-5 h-5 text-orange-500" />
            <h2 className="font-display font-bold text-lg">Arquivos gerados (versionados)</h2>
          </div>
          {arquivos.length === 0 ? (
            <p className="text-zinc-400 text-sm py-6 text-center">Nenhum arquivo gerado ainda.</p>
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="arquivos-lista">
              {arquivos.map((a) => (
                <div key={a.id} className="flex items-center gap-4 py-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-600 shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-zinc-800 truncate">{a.filename}</div>
                    <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.competencia}</span>
                      <span>v{a.versao}</span>
                      <span>{a.num_registros} registros</span>
                      <span>{new Date(a.gerado_em).toLocaleString("pt-BR")}</span>
                    </div>
                  </div>
                  <a
                    href={downloadArquivoUrl(a.id)}
                    data-testid={`download-${a.id}`}
                    className="inline-flex items-center gap-2 py-2 px-4 rounded-lg border border-zinc-200 hover:border-orange-400 hover:text-orange-600 text-sm font-semibold text-zinc-600 transition shrink-0"
                  >
                    <Download className="w-4 h-4" /> TXT
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
